// Sync orchestration. Local-first: the app never waits for any of this — the
// engine runs behind the loaded UI and reconciles with the server whenever
// there is a logged-in Steam session + connectivity. Ported from
// assetto-gallery's src/sync/engine.ts (_planning/referencia-assetto.md
// §6.3), adapted to plain JS + a tiny pub-sub status store instead of
// solid-js signals (no new dependency needed for React).
//
// When syncs happen:
//   - linkAccount() first time on this device / account switch -> full sync
//     (pull everything + upload the WHOLE local dataset)
//   - linkAccount() with an already-known account (session restore)
//                                         -> pull + push pending
//   - after each local write (markDirty) -> debounced push (4s)
//   - every 5 min while the tab is visible -> pull
//   - tab hidden / back online           -> immediate flush/sync
//
// Unlike assetto, config-strike's linkAccount() takes only the userId — the
// caller (SyncContext, driven by AuthContext) has no reliable way to tell
// "fresh login" from "restored session" apart, and it doesn't need to: a
// brand-new device or an account switch always deserves a fullSync, and an
// already-linked account just needs the everyday pull+push.

import { listOutbox, clearOutboxEntries, onOutboxChange, getSyncMeta, setSyncMeta } from './outbox'
import { collectRecord, collectAll, applyIncoming } from './collect'

const PUSH_DEBOUNCE_MS = 4_000
const PULL_INTERVAL_MS = 5 * 60_000
const PUSH_CHUNK = 500

let accountId = null
let pushTimer = null
let pullTimer = null
let syncing = false
let initialized = false

// --- status store: plain pub-sub, framework-agnostic ---
// "disabled" | "idle" | "syncing" | "offline" | "error"
let syncStatus = 'disabled'
let lastSyncAt = null
let syncError = null
const statusListeners = new Set()

function setStatus(next) {
    syncStatus = next
    for (const listener of statusListeners) listener()
}

export function getSyncStatus() {
    return syncStatus
}
export function getLastSyncAt() {
    return lastSyncAt
}
export function getSyncError() {
    return syncError
}
/** Subscribe to status changes; returns an unsubscribe fn. Works well with useSyncExternalStore. */
export function subscribeSyncStatus(listener) {
    statusListeners.add(listener)
    return () => statusListeners.delete(listener)
}

function isLoggedIn() {
    return accountId !== null
}

async function postPush(records) {
    for (let i = 0; i < records.length; i += PUSH_CHUNK) {
        const chunk = records.slice(i, i + PUSH_CHUNK)
        const res = await fetch('/api/sync/push', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ records: chunk }),
        })
        if (!res.ok) throw new Error(`push failed: ${res.status}`)
        await res.json() // "stale" results need no client action: the next pull brings the newer truth
    }
}

async function pushPending() {
    const entries = await listOutbox()
    if (entries.length === 0) return
    const records = (await Promise.all(entries.map(collectRecord))).filter((r) => r !== null)
    if (records.length > 0) await postPush(records)
    await clearOutboxEntries(entries)
}

async function pullSince() {
    let { cursor } = await getSyncMeta()
    for (;;) {
        const res = await fetch(`/api/sync/pull?since=${cursor}`, { credentials: 'same-origin' })
        if (!res.ok) throw new Error(`pull failed: ${res.status}`)
        const page = await res.json()
        for (const record of page.records) await applyIncoming(record)
        cursor = page.nextSince
        await setSyncMeta({ cursor })
        if (!page.hasMore) break
    }
}

/** Wraps a sync run with status/error bookkeeping; concurrent calls collapse. */
async function run(work) {
    if (!isLoggedIn() || syncing) return false
    syncing = true
    setStatus('syncing')
    try {
        await work()
        setStatus('idle')
        syncError = null
        const now = new Date().toISOString()
        lastSyncAt = now
        void setSyncMeta({ lastSyncAt: now })
        return true
    } catch (e) {
        const offline = typeof navigator !== 'undefined' && !navigator.onLine
        setStatus(offline ? 'offline' : 'error')
        syncError = e instanceof Error ? e.message : String(e)
        return false
    } finally {
        syncing = false
    }
}

/** Pull newer changes, then push whatever is pending. The everyday sync. */
export function syncNow() {
    return run(async () => {
        await pullSince()
        await pushPending()
    })
}

/**
 * First sync after linking an account on this device: pull the account's
 * whole dataset (cursor restarted at 0 by linkAccount), then upload the
 * ENTIRE local config dataset — not just the outbox — so configs created
 * before the account existed get online too. Pull runs first so server-side
 * tombstones/newer records settle before we collect what to upload.
 */
function fullSync() {
    return run(async () => {
        await pullSince()
        // Snapshot the outbox BEFORE collecting/pushing: a local write that
        // happens DURING the fullSync (long-running on a big dataset) lands
        // in the outbox after this snapshot and therefore survives the
        // clear below — it gets pushed on the next cycle instead of being
        // silently dropped.
        const entries = await listOutbox()
        await postPush(await collectAll())
        await clearOutboxEntries(entries)
    })
}

function schedulePush() {
    if (!isLoggedIn()) return
    if (pushTimer) clearTimeout(pushTimer)
    pushTimer = setTimeout(() => {
        pushTimer = null
        void run(pushPending)
    }, PUSH_DEBOUNCE_MS)
}

function flushPush() {
    if (!isLoggedIn()) return
    if (pushTimer) {
        clearTimeout(pushTimer)
        pushTimer = null
    }
    void run(pushPending)
}

/**
 * Links (or re-links) the sync engine to a Steam account on this device.
 * Decides fullSync vs. everyday syncNow purely from sync-meta.accountId:
 * no meta yet (brand-new device) or a different account than last time ->
 * fullSync; same account already linked (restored session) -> syncNow.
 */
export async function linkAccount(userId) {
    if (typeof window === 'undefined' || !userId) return
    const meta = await getSyncMeta()
    const switched = meta.accountId !== undefined && meta.accountId !== userId
    const firstLink = meta.accountId === undefined
    if (switched || firstLink) {
        // New account on this device: restart the cursor so we pull its
        // dataset from the beginning. Local configs stay and merge (LWW) on
        // the fullSync below.
        await setSyncMeta({ accountId: userId, cursor: 0 })
    }
    accountId = userId
    setStatus('idle')
    lastSyncAt = meta.lastSyncAt ?? null
    if (switched || firstLink) await fullSync()
    else await syncNow()
}

/** Call when the user logs out / AuthContext loses the user. */
export function unlinkAccount() {
    accountId = null
    setStatus('disabled')
}

/** Idempotent; call once from a top-level client provider (SyncContext). */
export function initSyncEngine() {
    if (initialized || typeof window === 'undefined') return
    initialized = true
    onOutboxChange(schedulePush)
    pullTimer = setInterval(() => {
        if (isLoggedIn() && document.visibilityState === 'visible') void syncNow()
    }, PULL_INTERVAL_MS)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushPush()
    })
    window.addEventListener('online', () => {
        if (isLoggedIn()) void syncNow()
    })
}
